import { createContext, memo, useCallback, useContext, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BroadcastChannelItem } from 'providers/contexts/extensions/Broadcast';
import { ChannelModal } from './ChannelModal';

/**
 * Modals context wrapper for Channels Search
 *
 * Mounts:
 * Edit/Create Modal for Channels
 *
 * Query params "openModal" for edit/create mode
 * - if openModal is set to a channel ID, opens edit modal
 * - if openModal is set to "create", opens create modal
 */

type ModalModes = 'create' | 'edit' | null;

export type ChannelsContextType = {
  // modal / qp state
  channelModalId: string | null;
  channelModalOpen: boolean;
  channelModalMode: ModalModes;
  selectedChannel: BroadcastChannelItem | null;
  handleCloseChannelModal: () => void;
  handleOpenCreateChannelModal: () => void;
  handleOpenEditChannelModal: (channel: BroadcastChannelItem) => void;
};

const ChannelsModalContext = createContext<ChannelsContextType | undefined>(undefined);

// Export for testing
export { ChannelsModalContext };

export const useChannelsModalContext = () => {
  const context = useContext(ChannelsModalContext);
  if (!context) {
    throw new Error(
      'useChannelsModalContext must be used within a ChannelsModalContextProvider',
    );
  }
  return context;
};

type ChannelsModalContextProviderProps = {
  children: React.ReactNode;
};

export const ChannelsModalContextProvider = memo(
  ({ children }: ChannelsModalContextProviderProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedChannel, setSelectedChannel] = useState<BroadcastChannelItem | null>(
      null,
    );

    /**
     * Modal state from query params
     * Edit modal fetch id:  /broadcast/channel/{id}/
     * - Create modal: openModal="create"
     * - Edit modal: openModal={channelId}
     * - Null/closed: no openModal qp
     */
    const channelModalId = searchParams.get('openModal') ?? null;
    const channelModalOpen = Boolean(channelModalId);
    const channelModalMode: ModalModes =
      channelModalId === 'create' ? 'create' : channelModalOpen ? 'edit' : null;

    /**
     * Remove the openModal qp to close the modal
     */
    const handleCloseChannelModal = useCallback((): void => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('openModal');
      setSearchParams(newSearchParams, { replace: true });
      setSelectedChannel(null);
    }, [searchParams, setSearchParams]);

    /**
     * Set the openModal qp in "create" mode
     */
    const handleOpenCreateChannelModal = useCallback((): void => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('openModal', 'create');
      setSearchParams(newSearchParams, { replace: true });
      setSelectedChannel(null);
    }, [searchParams, setSearchParams]);

    /**
     * Set the openModal qp in "edit" mode with channel ID
     */
    const handleOpenEditChannelModal = useCallback(
      (channel: BroadcastChannelItem): void => {
        if (!channel?.id) {
          console.warn('Invalid channel ID: channel must have a valid id');
          return;
        }
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('openModal', channel.id);
        setSearchParams(newSearchParams, { replace: true });
        setSelectedChannel(channel);
      },
      [searchParams, setSearchParams],
    );

    /**
     * Context value
     */
    const contextValue = useMemo(() => {
      return {
        channelModalId,
        channelModalOpen,
        channelModalMode,
        selectedChannel,
        handleCloseChannelModal,
        handleOpenCreateChannelModal,
        handleOpenEditChannelModal,
      };
    }, [
      channelModalId,
      channelModalOpen,
      channelModalMode,
      selectedChannel,
      handleCloseChannelModal,
      handleOpenCreateChannelModal,
      handleOpenEditChannelModal,
    ]);

    return (
      <ChannelsModalContext.Provider value={contextValue}>
        {channelModalOpen && (
          <ChannelModal
            open={channelModalOpen}
            mode={channelModalMode}
            channelId={channelModalId}
            channel={selectedChannel}
            onClose={handleCloseChannelModal}
          />
        )}
        {children}
      </ChannelsModalContext.Provider>
    );
  },
);
ChannelsModalContextProvider.displayName = 'ChannelsModalContextProvider';
